import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import MailLockIcon from '@mui/icons-material/MailLock';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularStatic from "./CircularStatic";
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrowserNotSupportedIcon from '@mui/icons-material/BrowserNotSupported';
import WarningIcon from '@mui/icons-material/Warning';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';

import { FETCH_PATH } from './environment'
import { useEffect, useState, useContext } from 'react';
import { PageContext } from './Page';
import HandleAsyncError from './HandleAsyncError';
import { getCookieValue } from './Cookies';

const statusToIcon = {
  "n": <BrowserNotSupportedIcon color="disabled" />,
  "v": <CheckCircleIcon sx={{ color: "#2ab672" }} />,
  "i": <ErrorIcon color="error" />,
  "w": <WarningIcon color="warning" />,
  "p": <HourglassBottomIcon color="disabled" />,
  "-": <Tooltip title='N/A'><BlockIcon color="disabled" /></Tooltip>,
  "info": <InfoIcon color="primary" />,
  "info-valid": <InfoIcon sx={{ color: "#2ab672" }}/>, // For passing header validation
};

function wrap_status(status, href) {
  if (status === 'n' || status === 'p' || status === '-') {
    return statusToIcon[status];
  } else {
    return <IconButton component={Link} href={href} target="_blank" onClick={evt => evt.stopPropagation()}>
      {statusToIcon[status]}
    </IconButton>;
  }
};

function computeRelativeDates(modelDate) {
  var offset = modelDate.getTimezoneOffset();
  modelDate = new Date(
    Date.UTC(
      modelDate.getUTCFullYear(),
      modelDate.getUTCMonth(),
      modelDate.getUTCDate(),
      modelDate.getUTCHours(),
      modelDate.getUTCMinutes() - offset,
      modelDate.getUTCSeconds()
    )
  );

  var now = new Date();
  var difference = (now - modelDate) / 1000; // convert from ms to s
  let divisor, unit;
  try {
    [divisor, unit] = [[3600 * 24 * 8, null], [3600 * 24 * 7, "weeks"], [3600 * 24, "days"], [3600, "hours"], [60, "minutes"], [1, "seconds"]].filter(a => difference / a[0] > 1.)[0];
  } catch {
    return ''
  }
  if (unit) {
    var relativeTime = Math.floor(difference / divisor);
    if (relativeTime == 1) { unit = unit.slice(0, -1); } // Remove the 's' in units if only 1
    return (<span className="abs_time" title={modelDate.toLocaleString()}>{relativeTime} {unit} ago</span>)
  } else {
    return modelDate.toLocaleString();
  }
};

function isRowAllowedToBeDeleted(row) {
      
  const isCompleted = row.progress >= 100;
  const isPending = row.progress === -1;
  const isError = row.progress === -2;
  return isCompleted || isPending || isError;
};

const headCells = [
  {
    id: 'filename',
    label: 'File Name',
  },
  {
    id: 'syntax',
    label: 'STEP Syntax',
    width: 100,
    align: 'center',
    tooltip: 'STEP Physical File Syntax'
  },
  {
    id: 'schema',
    label: 'IFC Schema',
    width: 100,
    align: 'center',
    tooltip: 'IFC Schema: inverse attributes, attribute types, cardinalities, where rules, function constraints'
  },
  {
    id: 'rules',
    label: 'Normative IFC Rules',
    width: 100,
    align: 'center',
    tooltip: 'Implementer Agreements and Informal Propositions'
  },
  {
    id: 'industry',
    label: 'Industry Practices',
    width: 100,
    align: 'center',
    tooltip: 'Checking the IFC file against common practice and sensible defaults. None of these checks render the IFC file invalid'
  },
  // disabled bSDD
  // {
  //   id: 'bsdd',
  //   label: 'bSDD Compliance',
  //   width: 100,
  //   align: 'center'
  // },
  {
    id: 'date',
    label: 'Date',
  },
  {
    id: 'download',
    label: '',
  }
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;

  return (
    <TableHead>
      <TableRow style={{ backgroundColor: '#e4eaee' }}>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': '',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || 'left'}
            padding='normal'
            width={headCell.width}
            sx={{fontWeight: 'bold'}}
          >
            {
              headCell.tooltip
                ? <Tooltip title={headCell.tooltip}>
                    <span style={{borderBottom: 'dotted 2px gray', display: 'inline-block'}}>{headCell.label}
                      <span style={{fontSize: '.83em', verticalAlign: 'super'}}>{headCell.tooltip ? 'ⓘ' : ''}</span>
                    </span>
                  </Tooltip>
                : headCell.label
            }
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar({ numSelected, onDelete, onRevalidate }) {

  return (
    <Toolbar
      sx={{
        pl: { sm: 1 },
        pr: { xs: 1, sm: 1 },
        backgroundColor: "none",
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 && (
        <Tooltip title="Delete">
          <IconButton onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>)}

      {numSelected > 0 ? (
        <Typography
          //sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          // sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        />
      )}
      
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function DashboardTable({ models }) {
  const [rows, setRows] = React.useState([])
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [count, setCount] = React.useState(0);
  const [deleted, setDeleted] = useState('');
  const [progress, setProgress] = useState(0);

  const context = useContext(PageContext);
  const handleAsyncError = HandleAsyncError();

  useEffect(() => {
    fetch(`${FETCH_PATH}/api/models_paginated/${page * rowsPerPage}/${page * rowsPerPage + rowsPerPage}`, { credentials: 'include', 'x-csrf-token': getCookieValue('csrftoken') })
      .then((response) => response.json())
      .then((json) => {
        setRows(json["models"]);
        setCount(json["count"]);
        if (json.models.some(m => (m.progress < 100))) {
          setTimeout(() => {setProgress(progress + 1)}, 5000)
        }
      }).catch(handleAsyncError);
  }, [page, rowsPerPage, progress, deleted, handleAsyncError]);


  const handleSelectAllClick = (event) => {

    const isChecked = event.target.checked;
    const isIndeterminate = (event.target.dataset.indeterminate === true);
    const allEnabledSelected = (selected.length > 0) && (selected.length === rows.filter(row => isRowAllowedToBeDeleted(row)).length);

    if (!isIndeterminate && !allEnabledSelected && isChecked) {
      const newSelected = rows.map((row) => isRowAllowedToBeDeleted(row) ? row.id : null).filter(row => row !== null);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {

    if (!isRowAllowedToBeDeleted(row)) {
      event.stopPropagation();
      return;
    }

    const selectedIndex = selected.indexOf(row.id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row.id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  useEffect(() => {

    if (deleted && deleted.length > 0) {

      fetch(`${FETCH_PATH}/api/delete/${deleted}`, {
        method: 'DELETE',
        headers: { 'x-csrf-token': getCookieValue('csrftoken') },
        credentials: 'include'
      })
        .then((response) => response.json())
        .then((json) => {
          setSelected([])
          setDeleted([])
        });
    }

  }, [deleted]);

  function onDelete() {
    if (window.confirm(`Delete ${selected.length} selected IFC file(s)? This action cannot be undone.`)) {
      setDeleted(selected.join(','))
    }
  }

  return (
    <Box sx={{ width: '100%', alignSelf: 'start' }}>

      <EnhancedTableToolbar numSelected={selected.length} onDelete={onDelete} />
      <TableContainer>
        <Table
          sx={{ 
            backgroundColor: 'white',
            '.MuiIconButton-root' : {
              // border: 'solid 1px rgba(0,0,0,30%)',
              boxShadow: '2px 2px 3px rgba(0,0,0,10%)'
            },
            '.MuiIconButton-root:hover' : {
              // border: 'solid 1px rgba(0,0,0,30%)',
              boxShadow: '2px 2px 3px rgba(0,0,0,30%)'
            },
            '*' : {
              lineHeight: '1em'
            }
          }}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
        >
          <EnhancedTableHead
            numSelected={selected.length}
            onSelectAllClick={handleSelectAllClick}
            rowCount={rows.length}
          />
          <TableBody>
            {rows.map((row, index) => {
              const isItemSelected = isSelected(row.id);
              const labelId = `enhanced-table-checkbox-${index}`;
              return (
                <TableRow
                  hover
                  onClick={(event) => handleClick(event, row)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  selected={isItemSelected}
                >
                  <TableCell padding="checkbox">
                  
                    <Tooltip title={!isRowAllowedToBeDeleted(row) ? "Unable to select file for deletion as it is still being processed. Validation can sometimes take hours, please do not re-upload the same file..." : ""}>
                    <span>
                      <Checkbox
                        color="primary"
                        checked={isItemSelected && isRowAllowedToBeDeleted(row)}
                        disabled={!isRowAllowedToBeDeleted(row)}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />                      
                    </span>
                  </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                  {row.filename}{" "}
                  {wrap_status(
                      // any header policy error (e.g. an invalid MVD) shows a warning,
                      // independent of status_header, which also gates the scorecards
                      row.status_header === 'i' ? "w" :
                      (row.header_validation?.validation_errors?.length > 0) ? "w" :
                      row.status_header === 'v' ? "info-valid" :
                      "info",
                      context.sandboxId
                        ? `/sandbox/report_file/${context.sandboxId}/${row.code}`
                        : `/report_file/${row.code}`
                    )}
                    &nbsp;
                    {row.status_signatures === 'v' && <IconButton component={Link} href={`/report_file/${row.code}#signatures`}>
                      <MailLockIcon sx={{ color: "#2ab672" }} />
                    </IconButton>}
                    {row.status_signatures === 'i' && <IconButton component={Link} href={`/report_file/${row.code}#signatures`}>
                      <MailLockIcon color="error" />
                    </IconButton>}
                </TableCell>
                  <TableCell align="center">
                    {wrap_status(row.status_syntax, context.sandboxId ? `/sandbox/report_syntax/${context.sandboxId}/${row.code}` : `/report_syntax/${row.code}`)}
                  </TableCell>
                  <TableCell align="center">
                    {wrap_status(row.status_schema, context.sandboxId ? `/sandbox/report_schema/${context.sandboxId}/${row.code}` : `/report_schema/${row.code}`)}
                  </TableCell>
                  <TableCell align="center">
                    {wrap_status(row.status_rules, context.sandboxId ? `/sandbox/report_rules/${context.sandboxId}/${row.code}` : `/report_rules/${row.code}`)}
                  </TableCell>
                  <TableCell align="center">
                    {wrap_status(row.status_ind, context.sandboxId ? `/sandbox/report_industry/${context.sandboxId}/${row.code}` : `/report_industry/${row.code}`)}
                  </TableCell>

                  {
                    (row.progress == 100) ?
                      <TableCell align="left">{computeRelativeDates(new Date(row.date))}</TableCell> :
                      <TableCell align="left">
                        {
                          (row.progress == -1) ? <Typography>{"in queue"}</Typography> :
                            ((row.progress == -2) ? <Typography>{"an error occured"}</Typography> : <CircularStatic value={row.progress} />)
                        }
                      </TableCell>
                  }

                  <TableCell align="left">
                  { 
                    (row.deleted ? '-' : <div>
                      <Button 
                        variant="text" 
                        disabled={!isRowAllowedToBeDeleted(row)} 
                        sx={{ 
                          textTransform: 'none', 
                          color: 'primary.main', 
                          '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent', }, 
                           }}
                        onClick={ (evt) => { 
                          evt.stopPropagation(); 
                          if (window.confirm('Are you sure you want to delete this IFC file? This action cannot be undone.'))
                              setDeleted(row.id);
                          }}>{'Delete IFC file'
                      }</Button>

                        <Tooltip title={!isRowAllowedToBeDeleted(row) ? "Unable to delete your IFC file as it is still being processed." : "Delete your IFC file immediately from the Validation Service. In any case, your file will be deleted after 2 weeks - as per our file retention policy."}>
                          <span style={{display: 'inline-block'}}>
                            <span style={{fontSize: '.83em', verticalAlign: 'super'}}>ⓘ</span>
                          </span>
                        </Tooltip>
                      </div>
                      )
                  }
                  </TableCell>
                </TableRow>
              );

            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)) }}
      />

    </Box>
  );
}